import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { useMentions } from '@/hooks/useMentions';
import { ROLE_LABELS } from '@/constants/roles';

interface MentionTextareaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const POPOVER_WIDTH = 256;
const MAX_VISIBLE_ITEMS = 6;
const ROW_HEIGHT = 32; // approx px per row

// Compute caret pixel coordinates inside a textarea via a hidden mirror div.
function getCaretCoordinates(el: HTMLTextAreaElement, position: number) {
  const div = document.createElement('div');
  const style = window.getComputedStyle(el);
  const props = [
    'boxSizing', 'width', 'height', 'overflowX', 'overflowY',
    'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
    'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    'fontStyle', 'fontVariant', 'fontWeight', 'fontStretch', 'fontSize',
    'fontSizeAdjust', 'lineHeight', 'fontFamily',
    'textAlign', 'textTransform', 'textIndent', 'textDecoration',
    'letterSpacing', 'wordSpacing', 'tabSize', 'whiteSpace', 'wordWrap',
  ] as const;
  div.style.position = 'absolute';
  div.style.visibility = 'hidden';
  div.style.whiteSpace = 'pre-wrap';
  div.style.wordWrap = 'break-word';
  div.style.top = '0';
  div.style.left = '-9999px';
  props.forEach((p) => {
    (div.style as any)[p] = (style as any)[p];
  });
  div.textContent = el.value.substring(0, position);
  const span = document.createElement('span');
  span.textContent = el.value.substring(position) || '.';
  div.appendChild(span);
  document.body.appendChild(div);
  const coords = { top: span.offsetTop, left: span.offsetLeft, height: span.offsetHeight };
  document.body.removeChild(div);
  return coords;
}

export const MentionTextarea: React.FC<MentionTextareaProps> = ({
  value,
  onChange,
  placeholder,
  className,
  disabled,
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { mentionables } = useMentions();

  const toDisplayValue = (text: string) => text.replace(/@\[([^\]]+)\]\([^:]+:[^)]+\)/g, '@$1');
  const toStoredValue = (displayText: string) => {
    let storedText = displayText;
    const mentionRegex = /@\[([^\]]+)\]\(([^:]+):([^)]+)\)/g;
    let match;
    while ((match = mentionRegex.exec(value)) !== null) {
      storedText = storedText.replace(`@${match[1]}`, match[0]);
    }
    return storedText;
  };
  const toStoredCursorPosition = (displayText: string, displayCursor: number) => {
    const displayBeforeCursor = displayText.substring(0, displayCursor);
    let storedCursor = displayCursor;
    const mentionRegex = /@\[([^\]]+)\]\(([^:]+):([^)]+)\)/g;
    let match;
    while ((match = mentionRegex.exec(value)) !== null) {
      const storedMention = match[0];
      const displayMention = `@${match[1]}`;
      const displayIndex = toDisplayValue(value.substring(0, match.index)).length;
      if (displayIndex + displayMention.length <= displayBeforeCursor.length) {
        storedCursor += storedMention.length - displayMention.length;
      }
    }
    return storedCursor;
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = toStoredValue(e.target.value);
    const newCursorPosition = toStoredCursorPosition(e.target.value, e.target.selectionStart);

    onChange(newValue);
    setCursorPosition(newCursorPosition);

    const textBeforeCursor = newValue.substring(0, newCursorPosition);
    const mentionMatch = textBeforeCursor.match(/@([^@\n\r\s]*)$/);

    if (mentionMatch) {
      setSearchTerm(mentionMatch[1]);
      setShowSuggestions(true);
      setHighlightIndex(0);
    } else {
      setShowSuggestions(false);
    }
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredSuggestions = mentionables
    .filter((m) => !normalizedSearch || m.name.toLowerCase().includes(normalizedSearch))
    .sort((a, b) => {
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();
      const aStarts = normalizedSearch && aName.startsWith(normalizedSearch);
      const bStarts = normalizedSearch && bName.startsWith(normalizedSearch);
      if (aStarts !== bStarts) return aStarts ? -1 : 1;
      return a.name.localeCompare(b.name);
    })
    .slice(0, 8);

  const insertMention = (mention: { id: string; name: string; type: 'user' | 'client'; role?: string }) => {
    const textBeforeCursor = value.substring(0, cursorPosition);
    const textAfterCursor = value.substring(cursorPosition);
    const mentionMatch = textBeforeCursor.match(/@([^@\n\r\s]*)$/);

    if (mentionMatch) {
      const beforeMention = textBeforeCursor.substring(0, textBeforeCursor.length - mentionMatch[0].length);
      const mentionText = `@[${mention.name}](${mention.type}:${mention.id}) `;
      const newValue = beforeMention + mentionText + textAfterCursor;

      onChange(newValue);
      setShowSuggestions(false);

      setTimeout(() => {
        if (textareaRef.current) {
          const displayBefore = toDisplayValue(beforeMention + mentionText);
          const newPosition = displayBefore.length;
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(newPosition, newPosition);
        }
      }, 0);
    }
  };

  // Reposition popover near caret whenever it's shown / value changes
  useLayoutEffect(() => {
    if (!showSuggestions || !textareaRef.current || !wrapperRef.current) return;
    const ta = textareaRef.current;
    const displayCursor = toDisplayValue(value.substring(0, cursorPosition)).length;
    const coords = getCaretCoordinates(ta, displayCursor);
    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    const taRect = ta.getBoundingClientRect();
    const offsetLeft = taRect.left - wrapperRect.left;
    const offsetTop = taRect.top - wrapperRect.top;
    let left = offsetLeft + coords.left - ta.scrollLeft;
    const top = offsetTop + coords.top + coords.height + 6 - ta.scrollTop;
    // Clamp inside textarea right edge
    const maxLeft = offsetLeft + ta.clientWidth - POPOVER_WIDTH - 4;
    if (left > maxLeft) left = Math.max(offsetLeft + 4, maxLeft);
    if (left < offsetLeft + 4) left = offsetLeft + 4;
    setPopoverPos({ top, left });
  }, [showSuggestions, cursorPosition, value, filteredSuggestions.length]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showSuggestions || filteredSuggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => (i + 1) % filteredSuggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => (i - 1 + filteredSuggestions.length) % filteredSuggestions.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      const pick = filteredSuggestions[highlightIndex];
      if (pick) insertMention(pick as any);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const displayValue = toDisplayValue(value);

  return (
    <div ref={wrapperRef} className="relative">
      <Textarea
        ref={textareaRef}
        value={displayValue}
        onChange={handleTextChange}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          // delay so clicks on the popover register first
          setTimeout(() => setShowSuggestions(false), 120);
        }}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
      />

      {showSuggestions && filteredSuggestions.length > 0 && (
        <div
          role="listbox"
          className="absolute z-[100] rounded-md border bg-popover text-popover-foreground shadow-lg overflow-y-auto py-1"
          style={{
            top: popoverPos.top,
            left: popoverPos.left,
            width: POPOVER_WIDTH,
            maxHeight: ROW_HEIGHT * MAX_VISIBLE_ITEMS + 8,
          }}
          onMouseDown={(e) => e.preventDefault()} // keep textarea focus on click
        >
          {filteredSuggestions.map((mention, idx) => {
            const isActive = idx === highlightIndex;
            const meta =
              mention.type === 'user'
                ? ROLE_LABELS[mention.role as keyof typeof ROLE_LABELS] || 'Staff'
                : 'Client';
            return (
              <button
                key={`${mention.type}-${mention.id}`}
                type="button"
                role="option"
                aria-selected={isActive}
                className={cn(
                  'flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left transition-colors',
                  isActive ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/60'
                )}
                onMouseEnter={() => setHighlightIndex(idx)}
                onClick={() => insertMention(mention as any)}
              >
                <span className="truncate text-sm font-medium">{mention.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{meta}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
